import { get } from 'lodash';
import Alert from '@lendi-ui/alert';
import getColour from '@lendi-ui/commons/colours';

import { ENTITY_TYPE, FIELD_PATHS, FIELD_VALUES, VISIT_CONTACT_TYPE } from '@funnels/constants';
import { useCustomer, useFormValues, useUpdateRelatedParties } from '@funnels/hooks';
import { Asset, Owner, PostProductSelectContract, RelatedParty, StoreApplicant } from '@funnels/types';
import {
  Accordion,
  ActionButtonsWrapper,
  Body,
  Container,
  ContentWrapper,
  Divider,
  FunnelGrid,
  FunnelGridUnit,
  Heading,
  HeadingWrapper,
  NextArrowAction,
} from '@funnels/ui-components';
import {
  ContactCompanyName,
  ContactCopier,
  ContactFirstName,
  ContactLastName,
  ContactPhoneNumber,
  ContactTitle,
  ContactType,
  DateOfPurchase,
} from '@funnels/ui-components/PostProductSelect';
import {
  getIndexFromCustomerId,
  getIndexFromId,
  getPPSPropertyDisplayFields,
  getPropertyContactCopy,
  hasPropertyContactToCopy,
  isExclusivelyOwnedByApplicantRole,
  maskPIIFullStory,
} from '@funnels/utils';

interface AccordionHeaderProps {
  header: string | JSX.Element;
  subHeader: string;
}

const AccordionHeader = ({ header, subHeader }: AccordionHeaderProps) => (
  <>
    <Heading size="xs">{header}</Heading>
    <Body size="xs" color={getColour('text', 'subtle')}>
      {subHeader}
    </Body>
  </>
);

interface PropertyLayoutProps {
  isFromSummary?: boolean;
}

export const PropertyLayout = (props: PropertyLayoutProps) => {
  const [formValues, setFormValue] = useFormValues();
  const { isFromSummary } = props;
  const customer = useCustomer();
  const customerApplicantIndex = getIndexFromCustomerId(
    get(formValues, FIELD_PATHS.APPLICANTS(), []),
    customer.data.id
  );
  const customerRole = get(formValues, FIELD_PATHS.APPLICANT_TYPE(customerApplicantIndex), FIELD_VALUES.BORROWER);
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const assets: Asset[] = get(formValues, FIELD_PATHS.ASSETS(), []);
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
  const propertyContracts = ppsContracts.filter(
    (pps) => get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ASSET
  );

  useUpdateRelatedParties(relatedParties);

  const heading = `We need more information about your property`;

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
      </HeadingWrapper>
      <ContentWrapper>
        <Accordion shouldHideAddButton onAdd={() => {}} addButtonText="">
          {propertyContracts.map((propertyContract) => {
            const assetId = get(
              { postProductSelect: [propertyContract] },
              FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
            );
            // Related party for property contact should meet 3 conditions:
            // 1. it has an asset id that match with the property
            // 2. the type of the related party should be either undefined (because of empty initialisation)
            //    or a type of VISIT_CONTACT_TYPE
            // 3. if current user is a borrower, the asset should not be owned by guarantor exclusively
            const relatedPartyIndex = relatedParties.findIndex(
              (relatedParty) =>
                get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_ASSET_ID(0)) === assetId &&
                (get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === undefined ||
                  Object.values(VISIT_CONTACT_TYPE).includes(
                    get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0))
                  ))
            );
            const assetIndex = getIndexFromId(assets, assetId);
            const owners: Owner[] = get(formValues, FIELD_PATHS.ASSET_OWNERS(assetIndex), []);
            if (
              customerRole === FIELD_VALUES.BORROWER &&
              isExclusivelyOwnedByApplicantRole(owners, applicants, FIELD_VALUES.GUARANTOR)
            ) {
              return null;
            }

            // omit no permission asset
            if (assetIndex === -1) {
              return null;
            }

            const asset = get(formValues, FIELD_PATHS.ASSET(assetIndex));
            const address: string = get({ assets: [asset] }, FIELD_PATHS.ASSET_FORMATTED_ADDRESS(0));
            const loanType = get({ assets: [asset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0))
              ? 'New Purchase'
              : 'Refinance';
            const relatedPartyType = get(formValues, FIELD_PATHS.RELATED_PARTY_TYPE(relatedPartyIndex));
            const questions = get(
              { postProductSelect: [propertyContract] },
              FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0)
            );

            const {
              hasDateOfPurchase,
              hasContactType,
              hasContactTitle,
              hasContactPhone,
              hasContactFirstName,
              hasContactLastName,
              hasContactCompanyName,
            } = getPPSPropertyDisplayFields(questions, relatedPartyType);

            return (
              <Accordion.Item
                key={propertyContract.entityId!}
                header={<AccordionHeader header={maskPIIFullStory(address)} subHeader={loanType} />}
              >
                <FunnelGrid direction="column">
                  {hasDateOfPurchase && (
                    <>
                      <FunnelGridUnit mt="sm">
                        <DateOfPurchase assetIndex={assetIndex} isOptional />
                      </FunnelGridUnit>
                      <FunnelGridUnit mt="sm">
                        <Divider />
                      </FunnelGridUnit>
                    </>
                  )}
                  {hasContactType && (
                    <>
                      <FunnelGridUnit mt="sm">
                        <Heading size="xs">Contact for access</Heading>
                      </FunnelGridUnit>
                      <FunnelGridUnit mt="sm">
                        <Alert variant="info">
                          We need to know who to contact for an external valuation of your property.
                        </Alert>
                      </FunnelGridUnit>
                      {hasPropertyContactToCopy(relatedParties, relatedPartyIndex) && (
                        <FunnelGridUnit mt="sm">
                          <ContactCopier
                            assets={assets}
                            relatedParties={relatedParties}
                            relatedPartyIndex={relatedPartyIndex}
                            copyContact={(fromIndex) => {
                              setFormValue(
                                FIELD_PATHS.RELATED_PARTY(relatedPartyIndex),
                                getPropertyContactCopy(relatedParties, fromIndex, relatedPartyIndex)
                              );
                            }}
                          />
                        </FunnelGridUnit>
                      )}
                    </>
                  )}
                  {hasContactType && (
                    <FunnelGridUnit mt="sm">
                      <ContactType relatedPartyIndex={relatedPartyIndex} isOptional />
                    </FunnelGridUnit>
                  )}
                  {(hasContactTitle || hasContactFirstName) && (
                    <FunnelGridUnit mt="sm">
                      <FunnelGrid gutter="xxs">
                        <FunnelGridUnit size={hasContactTitle && hasContactFirstName ? 0.3 : 1}>
                          {hasContactTitle && (
                            <ContactTitle
                              relatedPartyIndex={relatedPartyIndex}
                              isOptional
                              isDisabled={!relatedPartyType}
                            />
                          )}
                        </FunnelGridUnit>
                        <FunnelGridUnit size={hasContactTitle && hasContactFirstName ? 0.7 : 1}>
                          {hasContactFirstName && (
                            <ContactFirstName
                              relatedPartyIndex={relatedPartyIndex}
                              isOptional
                              isDisabled={!relatedPartyType}
                            />
                          )}
                        </FunnelGridUnit>
                      </FunnelGrid>
                    </FunnelGridUnit>
                  )}
                  {hasContactLastName && (
                    <FunnelGridUnit mt="sm">
                      <ContactLastName
                        relatedPartyIndex={relatedPartyIndex}
                        isOptional
                        isDisabled={!relatedPartyType}
                      />
                    </FunnelGridUnit>
                  )}
                  {hasContactPhone && (
                    <FunnelGridUnit mt="sm">
                      <ContactPhoneNumber
                        relatedPartyIndex={relatedPartyIndex}
                        isOptional
                        isDisabled={!relatedPartyType}
                      />
                    </FunnelGridUnit>
                  )}
                  {hasContactCompanyName && (
                    <FunnelGridUnit mt="sm">
                      <ContactCompanyName
                        relatedPartyIndex={relatedPartyIndex}
                        isOptional
                        isDisabled={!relatedPartyType}
                      />
                    </FunnelGridUnit>
                  )}
                </FunnelGrid>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </ContentWrapper>
      <ActionButtonsWrapper>
        <NextArrowAction variant="primary" isFullWidth customText={isFromSummary ? 'Save' : 'Next'} />
      </ActionButtonsWrapper>
    </Container>
  );
};
